import { useMemo } from 'react'

import Fuse, { Expression, IFuseOptions } from 'fuse.js'

import { DEFAULT_UNIVERSAL_SEARCH_CONFIG, UniversalSearchConfig } from '../../devTools/utils'
import { getEligibleSearchableItems } from '../eligibility'
import { EligibilityDeps } from '../eligibility/types'
import { STANDARD_ITEMS, STRICT_MATCH_ITEMS } from '../searchableItems'
import {
  CategoryPriorityConfig,
  DEFAULT_CATEGORY_PRIORITY,
  EligiblePagesDebugInfo,
  SearchableItemMetadata,
  UniversalSearchResult,
} from '../types'

/** Default debounce delay in milliseconds for search input */
export const DEFAULT_SEARCH_DEBOUNCE_MS = 500

/**
 * Builds Fuse.js options dynamically based on config.
 * - useExtendedSearch enables $and/$or logical operators for multi-word queries
 * - ignoreLocation matches anywhere in the string, not just the beginning
 */
const buildFuseOptions = (config: UniversalSearchConfig, threshold: number): IFuseOptions<SearchableItemMetadata> => ({
  useExtendedSearch: true,
  keys: [
    { name: 'displayName', weight: config.displayNameWeight },
    { name: 'keywords', weight: config.keywordsWeight },
  ],
  minMatchCharLength: config.minQueryLength,
  includeScore: true,
  ignoreLocation: true,
  threshold,
})

/**
 * Builds a Fuse.js query expression from user input.
 * - Single word: returns the word as-is for simple fuzzy search
 * - Multi-word: returns $and expression requiring all tokens to match
 *   in either displayName OR keywords
 *
 * @example
 * buildSearchQuery("therapy", 3) // returns "therapy"
 * buildSearchQuery("therapy couples", 3) // returns { $and: [{ $or: [...] }, { $or: [...] }] }
 */
const buildSearchQuery = (query: string, minQueryLength: number): string | Expression => {
  const tokens = query.split(/\s+/).filter((token) => token.length >= minQueryLength)

  if (tokens.length === 0) {
    return query
  }

  if (tokens.length === 1) {
    return tokens[0]
  }

  // Multi-word: require all tokens to match in displayName OR keywords
  // Each token must match in at least one of the searchable fields
  const andExpressions: Expression[] = tokens.map((token) => ({
    $or: [{ displayName: token } as Expression, { keywords: token } as Expression],
  }))

  return { $and: andExpressions }
}

export interface UseUniversalSearchOptions {
  /** Search query string */
  query: string
  /** Eligibility dependencies for pre-filtering */
  eligibilityDeps: EligibilityDeps
  /** Maximum number of results to return */
  limit?: number
  /** Optional config for Fuse.js settings (defaults to DEFAULT_UNIVERSAL_SEARCH_CONFIG) */
  config?: UniversalSearchConfig
  /** Optional category priority config (defaults to DEFAULT_CATEGORY_PRIORITY) */
  categoryPriority?: CategoryPriorityConfig
}

export interface UseUniversalSearchResult {
  /** Search results sorted by relevance */
  results: UniversalSearchResult[]
  /** Debug info about eligible pages */
  eligiblePagesDebugInfo: EligiblePagesDebugInfo
}

/**
 * Creates a comparator function for sorting search results.
 * Primary sort: Fuse.js score (lower = better match)
 * Secondary sort: Category priority (lower = higher rank)
 * Tertiary sort: Item priority within category (lower = higher rank)
 */
const createResultsComparator =
  (categoryPriority: CategoryPriorityConfig) =>
  (a: UniversalSearchResult, b: UniversalSearchResult): number => {
    // Primary: Score (lower is better)
    const scoreDiff = a.score - b.score
    if (Math.abs(scoreDiff) > 0.001) {
      return scoreDiff
    }

    // Secondary: Category priority (lower is higher rank)
    const aCategoryPriority = categoryPriority[a.item.category]
    const bCategoryPriority = categoryPriority[b.item.category]
    if (aCategoryPriority !== bCategoryPriority) {
      return aCategoryPriority - bCategoryPriority
    }

    // Tertiary: Item priority within category (lower is higher rank)
    const aItemPriority = a.item.priority ?? 999
    const bItemPriority = b.item.priority ?? 999
    return aItemPriority - bItemPriority
  }

/**
 * Hook for universal search with fuzzy matching and eligibility pre-filtering.
 *
 * The search pipeline:
 * 1. Pre-filter: Filter SEARCHABLE_ITEMS by user eligibility (O(n) once, memoized)
 * 2. Index: Create Fuse.js index from eligible pages (memoized, only rebuilds when eligibility changes)
 * 3. Match: Fuzzy search using Fuse.js (only when query.length >= 3)
 * 4. Rank: Sort by score, then category priority, then item priority
 *
 * @param options - Search options including query and eligibility deps
 * @returns Array of search results, sorted by relevance
 *
 * @example
 * ```tsx
 * const eligibilityDeps = useEligibilityDeps()
 * const [query, setQuery] = useState('')
 *
 * const results = useUniversalSearch({
 *   query,
 *   eligibilityDeps,
 *   limit: 10,
 * })
 * ```
 */
export const useUniversalSearch = ({
  query,
  eligibilityDeps,
  limit,
  config,
  categoryPriority,
}: UseUniversalSearchOptions): UseUniversalSearchResult => {
  const searchConfig = useMemo(() => ({ ...DEFAULT_UNIVERSAL_SEARCH_CONFIG, ...config }), [config])
  const categoryPriorityConfig = useMemo(
    () => ({ ...DEFAULT_CATEGORY_PRIORITY, ...categoryPriority }),
    [categoryPriority],
  )
  const { minQueryLength, strictThreshold, standardThreshold } = searchConfig
  // Step 1: Pre-filter pages by eligibility (memoized on eligibilityDeps)
  // Uses pre-split arrays from searchablePages.ts for strict vs standard matching
  const eligibleStrictPages = useMemo(
    () => getEligibleSearchableItems(eligibilityDeps, STRICT_MATCH_ITEMS),
    [eligibilityDeps],
  )
  const eligibleStandardPages = useMemo(
    () => getEligibleSearchableItems(eligibilityDeps, STANDARD_ITEMS),
    [eligibilityDeps],
  )

  // Build debug info from eligible pages (memoized)
  const eligiblePagesDebugInfo: EligiblePagesDebugInfo = useMemo(() => {
    const allEligible = [...eligibleStrictPages, ...eligibleStandardPages]
    return {
      totalCount: allEligible.length,
      strictCount: eligibleStrictPages.length,
      standardCount: eligibleStandardPages.length,
      pageNames: allEligible.map((page) => page.displayName),
    }
  }, [eligibleStrictPages, eligibleStandardPages])

  // Step 2: Create memoized Fuse instances (rebuilds when eligible pages or config changes)
  const strictFuseOptions = useMemo(
    () => buildFuseOptions(searchConfig, strictThreshold),
    [searchConfig, strictThreshold],
  )
  const standardFuseOptions = useMemo(
    () => buildFuseOptions(searchConfig, standardThreshold),
    [searchConfig, standardThreshold],
  )
  const strictFuse = useMemo(
    () => new Fuse(eligibleStrictPages, strictFuseOptions),
    [eligibleStrictPages, strictFuseOptions],
  )
  const standardFuse = useMemo(
    () => new Fuse(eligibleStandardPages, standardFuseOptions),
    [eligibleStandardPages, standardFuseOptions],
  )

  // Step 4 & 5: Search both indexes and combine results (memoized on query and fuse instances)
  const results = useMemo(() => {
    const trimmedQuery = query.trim()

    // Early return for short queries
    if (trimmedQuery.length < minQueryLength) {
      return []
    }

    // Build search query (handles multi-word tokenization)
    const searchQuery = buildSearchQuery(trimmedQuery, minQueryLength)

    // Perform fuzzy search on both indexes
    const strictResults = strictFuse.search(searchQuery)
    const standardResults = standardFuse.search(searchQuery)

    // Map to UniversalSearchResult format and combine
    const searchResults: UniversalSearchResult[] = [
      ...strictResults.map((result) => ({
        item: result.item,
        score: result.score ?? 1,
      })),
      ...standardResults.map((result) => ({
        item: result.item,
        score: result.score ?? 1,
      })),
    ]

    // Sort combined results by score → category priority → item priority
    const compareResults = createResultsComparator(categoryPriorityConfig)
    searchResults.sort(compareResults)

    return limit ? searchResults.slice(0, limit) : searchResults
  }, [query, strictFuse, standardFuse, limit, minQueryLength, categoryPriorityConfig])

  return { results, eligiblePagesDebugInfo }
}
